"use client"

import { useEffect, useRef } from "react"
import { usePathname } from "next/navigation"
import { hasConsent } from "@/lib/cookies/consent"
import { markAnalyticsReady } from "@/lib/analytics/track"
import { installMetaPixel, isValidMetaPixelId } from "@/lib/analytics/meta-pixel"

// ---------------------------------------------------------------------------
// MetaPixel: consent-gated Meta (Facebook/Instagram) pixel.
//
//  - Platform feature, off by default: no-op unless NEXT_PUBLIC_META_PIXEL_ID is
//    set (per-customer build ARG, see Dockerfile + next.config.ts CSP).
//  - Gated on the `marketing` cookie category, never on `analytics`: an ad
//    platform pixel is advertising under GDPR. Until that category is granted,
//    fbevents.js is not requested and nothing reaches Meta.
//  - Reacts to the banner's `cookie-consent-changed` event without a reload:
//    grant loads the pixel (or re-grants it), withdrawal calls
//    fbq('consent','revoke') so an already-loaded pixel stops sending.
//  - PageView on first load and on every client-side route change.
//  - Ecommerce events (ViewContent, AddToCart, InitiateCheckout, Purchase) are
//    sent by src/lib/analytics/track.ts once this component marks Meta ready.
//
// Renders nothing, so there is no server/client markup to mismatch (React #418).
// ---------------------------------------------------------------------------

const CONSENT_CHANGED_EVENT = "cookie-consent-changed"

export default function MetaPixel() {
  const pixelId = process.env.NEXT_PUBLIC_META_PIXEL_ID?.trim()
  const pathname = usePathname()
  const pathnameRef = useRef(pathname)
  const installedRef = useRef(false)
  const lastTrackedPathRef = useRef<string | null>(null)

  useEffect(() => {
    pathnameRef.current = pathname
  }, [pathname])

  useEffect(() => {
    if (!isValidMetaPixelId(pixelId)) return

    const syncConsent = () => {
      if (!hasConsent("marketing")) {
        if (installedRef.current) window.fbq?.("consent", "revoke")
        return
      }
      if (installMetaPixel(pixelId)) {
        installedRef.current = true
        lastTrackedPathRef.current = pathnameRef.current
        window.fbq?.("track", "PageView")
      } else if (installedRef.current) {
        window.fbq?.("consent", "grant")
      }
      markAnalyticsReady("meta")
    }

    syncConsent()
    window.addEventListener(CONSENT_CHANGED_EVENT, syncConsent)
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, syncConsent)
  }, [pixelId])

  useEffect(() => {
    if (!installedRef.current || lastTrackedPathRef.current === pathname) return
    lastTrackedPathRef.current = pathname
    if (hasConsent("marketing")) window.fbq?.("track", "PageView")
  }, [pathname])

  return null
}
